import {
  getAggregatePeriodForLowerBucketCountLimit,
  getAggregatePeriodForUpperBucketCountLimit,
} from '@modules/common/utils';
import {
  MAX_DAILY_BUCKET_COUNT_FOR_TREND_CHART,
  MAX_WEEKLY_BUCKET_COUNT_FOR_TREND_CHART,
  MIN_TIME_BUCKET_COUNT_FOR_TREND_CHART,
} from '@modules/quantify/constants';
import { getNewPagerNumber, isValidQuery } from '../utils';
import { QuantifyActionType } from './quantifyActionType';
import { quantifyInitialState } from './quantifyInitialState';
import { QuantifyAction, QuantifyState } from './types';

export function quantifyReducer(
  state: QuantifyState,
  action: QuantifyAction,
): QuantifyState {
  const { type, payload } = action;
  switch (type) {
    case QuantifyActionType.INIT:
      return {
        ...state,
        ...payload,
      };
    case QuantifyActionType.INIT_QUERY: {
      return {
        ...state,
        query: {
          ...state.query,
          ...payload,
        },
        visual: {
          ...state.visual,
          timeRangeTraversal: {
            originalDuration: payload?.duration ?? state.query.duration,
            traverseCount: 0,
          },
          hasQueryChanged: false,
          pageNumber: 1,
        },
        reportData: { ...quantifyInitialState.reportData },
      };
    }
    case QuantifyActionType.SET_QUERY: {
      let aggregatePeriod = state.metadata.aggregatePeriod;

      if (payload?.duration) {
        const aggregatePeriodForUpperBucketCountLimit =
          getAggregatePeriodForUpperBucketCountLimit({
            currentAggregatePeriod: state.metadata.aggregatePeriod,
            dateRange: payload.duration,
            upperBucketCountLimit: {
              weekly: MAX_WEEKLY_BUCKET_COUNT_FOR_TREND_CHART,
              daily: MAX_DAILY_BUCKET_COUNT_FOR_TREND_CHART,
            },
          });

        aggregatePeriod = getAggregatePeriodForLowerBucketCountLimit({
          currentAggregatePeriod: aggregatePeriodForUpperBucketCountLimit,
          dateRange: payload.duration,
          lowerBucketCountLimit: MIN_TIME_BUCKET_COUNT_FOR_TREND_CHART,
        });
      }

      return {
        ...state,
        query: {
          ...state.query,
          ...payload,
        },
        visual: {
          ...state.visual,
          timeRangeTraversal: {
            originalDuration:
              payload?.duration ??
              state.visual.timeRangeTraversal.originalDuration,
            traverseCount: 0,
          },
          hasQueryChanged: isValidQuery({ ...state.query, ...payload }),
          pageNumber: 1,
          searchTerm: '',
        },
        reportData: { ...quantifyInitialState.reportData },
        metadata: {
          ...state.metadata,
          aggregatePeriod,
        },
      };
    }
    case QuantifyActionType.RESET_QUERY: {
      return {
        query: { ...quantifyInitialState.query },
        metadata: { ...quantifyInitialState.metadata },
        visual: { ...quantifyInitialState.visual },
        reportData: { ...quantifyInitialState.reportData },
        misc: { ...quantifyInitialState.misc },
      };
    }
    case QuantifyActionType.INIT_METADATA: {
      return {
        ...state,
        metadata: {
          ...state.metadata,
          ...payload,
        },
        visual: {
          ...state.visual,
          hasMetadataChanged: false,
          pageNumber: getNewPagerNumber({
            newMedataData: payload,
            oldMedataData: state.metadata,
            pageNumber: state.visual.pageNumber,
          }),
        },
      };
    }
    case QuantifyActionType.SET_METADATA: {
      return {
        ...state,
        metadata: {
          ...state.metadata,
          ...payload,
          ...(payload.compareQBField
            ? {
                compareQBField: {
                  ...state.metadata.compareQBField,
                  ...payload.compareQBField,
                },
              }
            : {}),
          ...(payload.compareDurationField
            ? {
                compareDurationField: {
                  ...state.metadata.compareDurationField,
                  ...payload.compareDurationField,
                },
              }
            : {}),
        },
        visual: {
          ...state.visual,
          hasMetadataChanged: true,
          pageNumber: getNewPagerNumber({
            newMedataData: payload,
            oldMedataData: state.metadata,
            pageNumber: state.visual.pageNumber,
          }),
        },
      };
    }
    case QuantifyActionType.SET_VISUAL: {
      return {
        ...state,
        visual: {
          ...state.visual,
          ...payload,
        },
      };
    }
    case QuantifyActionType.SET_REPORT_DATA: {
      const { payload } = action;

      return {
        ...state,
        reportData: {
          ...state.reportData,
          quantify: { ...state.reportData.quantify, ...payload.quantify },
          compare: { ...state.reportData.compare, ...payload.compare },
        },
      };
    }

    case QuantifyActionType.SET_MISC: {
      return {
        ...state,
        misc: {
          ...state.misc,
          ...payload,
        },
      };
    }

    default:
      return state;
  }
}
